'use client';

import { useRef } from 'react';

import { Upload } from 'lucide-react';

import { Button } from '@kit/ui/button';
import { Trans } from '@kit/ui/trans';

import { UploadFileResult, useFileUploader } from '../hooks/use-file-uploader';

/**
 * Props for the UploadFileButton component
 */
type UploadFileButtonProps = {
  /**
   * Optional callback when files are selected
   */
  onSelect?: (files: { file: File; key: string; bucketName: string }[]) => void;

  /**
   * The account ID to associate the uploads with
   */
  accountId: string;

  /**
   * The S3 bucket name where files will be uploaded
   */
  bucketName: string;

  /**
   * Template string for generating the S3 key path
   * @example '${accountId}/uploads/${file.name}' -> 'abc123/uploads/image.jpg'
   * @example '${accountId}/raw/_fileName_' -> 'abc123/raw/image.jpg'
   * @example '${accountId}/_fileId_/raw/_fileName_' -> 'abc123/123abc/raw/image.jpg'
   */
  keyFormat: string;

  /**
   * Whether to allow multiple file selection
   * @default false
   */
  multiple?: boolean;

  /**
   * MIME types or file extensions to accept
   */
  accept?: string;

  /**
   * Button variant
   * @default 'default'
   */
  variant?:
    | 'default'
    | 'destructive'
    | 'outline'
    | 'secondary'
    | 'ghost'
    | 'link';

  /**
   * Button size
   * @default 'default'
   */
  size?: 'default' | 'sm' | 'lg' | 'icon';

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Custom button content
   */
  children?: React.ReactNode;

  /**
   * Callback when uploads complete
   */
  onComplete?: (results: UploadFileResult[]) => void;

  /**
   * The key of the upload session.
   * Used to resume uploads.
   */
  uploadKey?: string;
};

/**
 * A button component that opens a file picker and initiates uploads.
 *
 * @example
 * ```tsx
 * <UploadFileButton
 *   accountId="123"
 *   bucketName="my-bucket"
 *   keyFormat="${accountId}/uploads/${file.name}"
 *   multiple={true}
 *   accept="image/*,.pdf"
 *   onComplete={(results) => console.log('Upload complete:', results)}
 * />
 * ```
 */
export function UploadFileSelectorButton({
  onSelect,
  accountId,
  bucketName,
  keyFormat,
  multiple = false,
  accept,
  variant = 'default',
  size = 'default',
  className,
  children,
  uploadKey,
}: UploadFileButtonProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { handleFileSelect } = useFileUploader();

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);

    const fileData = selectedFiles.map((file) => ({
      file,
      key: keyFormat
        .replace('_fileName_', file.name)
        .replace('_fileId_', crypto.randomUUID()),
      bucketName,
    }));

    onSelect?.(fileData);

    await handleFileSelect(fileData, { uploadKey, accountId });
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleChange}
        multiple={multiple}
        accept={accept}
      />
      <Button
        variant={variant}
        size={size}
        className={className}
        onClick={handleClick}
      >
        {children || (
          <>
            <Upload className="mr-2 h-4 w-4" />
            <Trans i18nKey="views:uploadManager.upload" />
          </>
        )}
      </Button>
    </>
  );
}
